import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Wrench, Home, Tent, Dog } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroVideo from "@/assets/hero-video.mp4";
import { isRTL, useLanguageStore } from "@/stores/languageStore";

export const HeroSection = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  const arrowSpacing = rtl ? "mr-2" : "ml-2";

  return (
    <section className="relative min-h-[80vh] flex items-center overflow-hidden">
      {/* Video Background */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline 
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => console.error('Hero video failed to load:', e)}
      >
        <source src={heroVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-charcoal/70" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-white space-y-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>

            {/* Updated Headline */}
            <div className="space-y-5">
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                <span className="text-white">{t("hero.headlineLead")}</span>
                <span className="text-primary"> {t("hero.headlineAccent")}</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-lg leading-relaxed">
                {t("hero.description")}
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 pt-2">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                  <span className="text-primary text-lg">✔</span>
                  <span className="text-sm font-medium text-white">{t("hero.badge1")}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                  <span className="text-primary text-lg">✔</span>
                  <span className="text-sm font-medium text-white">{t("hero.badge2")}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                  <span className="text-primary text-lg">✔</span>
                  <span className="text-sm font-medium text-white">{t("hero.badge3")}</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button size="lg" className="gradient-gold text-white shadow-elegant">
                  {t("hero.primaryCta")}
                  <ArrowIcon className={`${arrowSpacing} h-5 w-5`} />
                </Button>
              </Link>
              <Link to="/why-buy-from-us">
                <Button size="lg" variant="outline" className="border-gold/50 bg-gold/10 text-gold hover:bg-gold/20 backdrop-blur-sm">
                  {t("hero.whyBuyCta")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Features */}
          <div className="hidden lg:grid grid-cols-2 gap-6 animate-fade-in" style={{ animationDelay: "0.4s" }}>
            <FeatureCard icon={Wrench} title={t("hero.feature1Title")} description={t("hero.feature1Desc")} />
            <FeatureCard icon={Home} title={t("hero.feature2Title")} description={t("hero.feature2Desc")} />
            <FeatureCard icon={Tent} title={t("hero.feature3Title")} description={t("hero.feature3Desc")} />
            <FeatureCard icon={Dog} title={t("hero.feature4Title")} description={t("hero.feature4Desc")} />
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) => (
  <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
    <div className="w-12 h-12 rounded-lg gradient-gold flex items-center justify-center mb-4">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <h2 className="font-display text-lg font-semibold text-white mb-2">{title}</h2>
    <p className="text-sm text-white/60">{description}</p>
  </div>
);
